/**
 * Helpers for the dashboard's `?asOf=YYYY-MM-DD` point-in-time view. The
 * param names a UTC calendar day (matching how daily metrics are stored),
 * while the date picker works in the browser's local calendar — these
 * helpers convert between the two without the off-by-one-day shifts that
 * `new Date('YYYY-MM-DD')` + local formatting produce in negative-offset
 * timezones.
 */

const AS_OF_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Strict YYYY-MM-DD calendar-date check (rejects e.g. 2026-02-31). */
export function isValidAsOfParam(value: string): boolean {
  if (!AS_OF_RE.test(value)) return false;
  const parsed = Date.parse(value + 'T00:00:00.000Z');
  return !isNaN(parsed) && new Date(parsed).toISOString().slice(0, 10) === value;
}

/** Last millisecond of the selected UTC day — the history endTime bound. */
export function asOfDayEndMs(asOf: string): number {
  return Date.parse(asOf + 'T00:00:00.000Z') + 24 * 60 * 60 * 1000 - 1;
}

/** Calendar-day param for a picker-selected (local) Date. */
export function dateToAsOfParam(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Local-calendar Date for the param, for feeding back into the picker. */
export function asOfParamToCalendarDate(asOf: string): Date {
  const [year, month, day] = asOf.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Today's UTC calendar day as a param string. The as-of boundary: days
 * before this are selectable history; this day and later mean "live".
 */
export function utcTodayParam(): string {
  return new Date().toISOString().slice(0, 10);
}

/** Human label for the selected day, e.g. "Aug 1, 2026". */
export function formatAsOfLabel(asOf: string): string {
  return new Date(asOf + 'T00:00:00.000Z').toLocaleDateString('en-US', {
    timeZone: 'UTC',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
